import { useState } from 'react'
import type {
  SectionGranularConfig,
  AccessLevel,
  RegionFilter,
} from '../types/permissions'
import { AVAILABLE_SECTIONS } from '../types/permissions'

// ─── US / Canada states for the region picker ────────────────────────
const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC',
]
const CA_PROVINCES = ['AB','BC','MB','NB','NL','NS','NT','NU','ON','PE','QC','SK','YT']
const ALL_REGIONS = [...US_STATES, ...CA_PROVINCES].sort()

const COUNTRIES = ['US', 'CA', 'UK', 'AU', 'DE', 'FR', 'JP', 'BR', 'MX', 'Other']

// ─── Props ───────────────────────────────────────────────────────────

type Props = {
  /** Which sections are enabled */
  selectedSections: string[]
  onToggleSection: (id: string) => void
  /** Granular config for each enabled section */
  sectionConfig: SectionGranularConfig
  onUpdateConfig: (config: SectionGranularConfig) => void
  /** Role of the team member (support is always view-only) */
  role: 'manager' | 'support'
}

// ─── Component ───────────────────────────────────────────────────────

export default function SectionPermissionConfig({
  selectedSections,
  onToggleSection,
  sectionConfig,
  onUpdateConfig,
  role,
}: Props) {
  const [expandedSection, setExpandedSection] = useState<string | null>(null)

  const updateSectionField = (sectionId: string, field: string, value: any) => {
    const current = (sectionConfig as any)[sectionId] || {}
    onUpdateConfig({
      ...sectionConfig,
      [sectionId]: { ...current, [field]: value },
    })
  }

  const updateNestedField = (sectionId: string, parent: string, field: string, value: any) => {
    const current = (sectionConfig as any)[sectionId] || {}
    const parentObj = current[parent] || {}
    onUpdateConfig({
      ...sectionConfig,
      [sectionId]: {
        ...current,
        [parent]: { ...parentObj, [field]: value },
      },
    })
  }

  const getAccessLevel = (sectionId: string): AccessLevel => {
    if (role === 'support') return 'view'
    return ((sectionConfig as any)[sectionId]?.accessLevel as AccessLevel) || 'edit'
  }

  const getFinancialAccess = (sectionId: string): boolean => {
    if (role === 'support') return false
    return (sectionConfig as any)[sectionId]?.financialAccess ?? true
  }

  const getRegionFilter = (sectionId: string): RegionFilter => {
    return (sectionConfig as any)[sectionId]?.regionFilter ?? {
      enabled: false,
      cities: [],
      states: [],
      countries: [],
    }
  }

  return (
    <div>
      <label className="block text-sm font-medium mb-3 text-gray-700 dark:text-gray-300">
        Section Access & Permissions
        <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
          ({selectedSections.length} selected)
        </span>
      </label>

      <div className="space-y-2 max-h-[500px] overflow-y-auto p-1">
        {AVAILABLE_SECTIONS.map((section) => {
          const isSelected = selectedSections.includes(section.id)
          const isExpanded = expandedSection === section.id && isSelected
          const hasGranularOptions = section.granularOptions && section.granularOptions.length > 0

          return (
            <div
              key={section.id}
              className={`rounded-lg border transition-all ${
                isSelected
                  ? 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                  : 'border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 opacity-60'
              }`}
            >
              {/* Section header row */}
              <div className="flex items-center gap-3 p-3">
                {/* Checkbox */}
                <div className="flex-shrink-0 relative flex items-center justify-center">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggleSection(section.id)}
                    className={`w-4 h-4 rounded border-2 transition-all duration-200 ease-in-out appearance-none cursor-pointer focus:ring-2 focus:ring-offset-0 ${
                      isSelected
                        ? 'bg-gray-900 dark:bg-gray-100 border-gray-900 dark:border-gray-100'
                        : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 hover:border-gray-400'
                    }`}
                  />
                  {isSelected && (
                    <svg className="absolute w-3 h-3 pointer-events-none" viewBox="0 0 20 20" fill="none">
                      <path
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        className="fill-white dark:fill-gray-900"
                      />
                    </svg>
                  )}
                </div>

                {/* Label */}
                <div className="flex-1 min-w-0">
                  <div className={`text-sm transition-colors ${
                    isSelected ? 'text-gray-900 dark:text-gray-100 font-medium' : 'text-gray-500'
                  }`}>
                    {section.label}
                  </div>
                  <div className={`text-xs mt-0.5 transition-colors ${
                    isSelected ? 'text-gray-500 dark:text-gray-400' : 'text-gray-400 dark:text-gray-600'
                  }`}>
                    {section.description}
                  </div>
                </div>

                {/* Expand button for granular controls */}
                {isSelected && hasGranularOptions && (
                  <button
                    type="button"
                    onClick={() => setExpandedSection(isExpanded ? null : section.id)}
                    className="flex-shrink-0 p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-500 dark:text-gray-400"
                    title="Configure granular permissions"
                  >
                    <svg
                      className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                )}

                {/* Quick access level pill */}
                {isSelected && hasGranularOptions && (
                  <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${
                    getAccessLevel(section.id) === 'edit'
                      ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                      : 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                  }`}>
                    {getAccessLevel(section.id) === 'edit' ? 'Edit' : 'View'}
                  </span>
                )}
              </div>

              {/* Granular controls (expanded) */}
              {isExpanded && (
                <div className="px-4 pb-4 pt-1 border-t border-gray-100 dark:border-gray-700 space-y-4">
                  {/* Access Level */}
                  {section.granularOptions?.some(o => o.type === 'accessLevel') && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                        Access Level
                      </label>
                      <div className="flex gap-2">
                        {(['view', 'edit'] as AccessLevel[]).map((level) => {
                          const isActive = getAccessLevel(section.id) === level
                          const isDisabled = role === 'support' && level === 'edit'
                          return (
                            <button
                              key={level}
                              type="button"
                              disabled={isDisabled}
                              onClick={() => updateSectionField(section.id, 'accessLevel', level)}
                              className={`px-3 py-1.5 text-xs rounded-md font-medium transition-all ${
                                isActive
                                  ? level === 'edit'
                                    ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 ring-1 ring-green-300 dark:ring-green-700'
                                    : 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 ring-1 ring-blue-300 dark:ring-blue-700'
                                  : isDisabled
                                    ? 'bg-gray-50 dark:bg-gray-800 text-gray-300 dark:text-gray-600 cursor-not-allowed'
                                    : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                              }`}
                            >
                              {level === 'view' ? 'View Only' : 'Full Edit'}
                            </button>
                          )
                        })}
                      </div>
                      {role === 'support' && (
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                          Support members are always view-only
                        </p>
                      )}
                    </div>
                  )}

                  {/* Financial Access */}
                  {section.granularOptions?.some(o => o.type === 'financialAccess') && (
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs font-medium text-gray-600 dark:text-gray-400">
                          Financial Data
                        </div>
                        <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                          {section.id === 'shows'
                            ? 'Guarantees, revenue, payout info'
                            : section.id === 'music'
                            ? 'Royalties, stream revenue'
                            : 'Financial metrics and reports'}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => updateSectionField(section.id, 'financialAccess', !getFinancialAccess(section.id))}
                        disabled={role === 'support'}
                        className={`w-10 h-5.5 rounded-full transition-all flex-shrink-0 ${
                          getFinancialAccess(section.id)
                            ? 'bg-green-500'
                            : 'bg-gray-300 dark:bg-gray-600'
                        } ${role === 'support' ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <div
                          className={`w-4.5 h-4.5 rounded-full bg-white shadow transition-transform ${
                            getFinancialAccess(section.id) ? 'translate-x-5' : 'translate-x-0.5'
                          }`}
                          style={{ width: 18, height: 18, marginTop: 1 }}
                        />
                      </button>
                    </div>
                  )}

                  {/* Ticket Access (Shows only) */}
                  {section.id === 'shows' && (
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs font-medium text-gray-600 dark:text-gray-400">
                          Ticket Data
                        </div>
                        <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                          Ticket counts, sold percentage, capacity
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          updateSectionField(
                            'shows',
                            'ticketAccess',
                            !((sectionConfig as any).shows?.ticketAccess ?? true)
                          )
                        }
                        className={`w-10 rounded-full transition-all flex-shrink-0 ${
                          (sectionConfig as any).shows?.ticketAccess !== false
                            ? 'bg-green-500'
                            : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                        style={{ height: 20 }}
                      >
                        <div
                          className={`rounded-full bg-white shadow transition-transform ${
                            (sectionConfig as any).shows?.ticketAccess !== false
                              ? 'translate-x-5'
                              : 'translate-x-0.5'
                          }`}
                          style={{ width: 18, height: 18, marginTop: 1 }}
                        />
                      </button>
                    </div>
                  )}

                  {/* Region Filter (Shows only) */}
                  {section.granularOptions?.some(o => o.type === 'regionFilter') && (
                    <RegionFilterConfig
                      regionFilter={getRegionFilter(section.id)}
                      onUpdate={(rf) => updateSectionField(section.id, 'regionFilter', rf)}
                    />
                  )}

                  {/* Date Filter */}
                  {section.granularOptions?.some(o => o.type === 'dateFilter') && (
                    <DateFilterConfig
                      sectionId={section.id}
                      enabled={(sectionConfig as any)[section.id]?.dateFilter?.enabled ?? false}
                      from={(sectionConfig as any)[section.id]?.dateFilter?.from ?? ''}
                      to={(sectionConfig as any)[section.id]?.dateFilter?.to ?? ''}
                      onToggle={(enabled) => updateNestedField(section.id, 'dateFilter', 'enabled', enabled)}
                      onChangeFrom={(v) => updateNestedField(section.id, 'dateFilter', 'from', v)}
                      onChangeTo={(v) => updateNestedField(section.id, 'dateFilter', 'to', v)}
                    />
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {selectedSections.length === 0 && (
        <p className="text-xs text-red-600 dark:text-red-400 mt-2">
          Please select at least one section
        </p>
      )}
    </div>
  )
}

// ─── Sub-components ──────────────────────────────────────────────────

function RegionFilterConfig({
  regionFilter,
  onUpdate,
}: {
  regionFilter: RegionFilter
  onUpdate: (rf: RegionFilter) => void
}) {
  const [cityInput, setCityInput] = useState('')

  const addItem = (key: 'states' | 'cities' | 'countries', value: string) => {
    const trimmed = value.trim()
    if (!trimmed) return
    const current = regionFilter[key] || []
    if (current.includes(trimmed)) return
    onUpdate({ ...regionFilter, [key]: [...current, trimmed] })
  }

  const removeItem = (key: 'states' | 'cities' | 'countries', value: string) => {
    onUpdate({
      ...regionFilter,
      [key]: (regionFilter[key] || []).filter((v) => v !== value),
    })
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-xs font-medium text-gray-600 dark:text-gray-400">
            Region Filter
          </div>
          <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            Restrict to specific locations
          </div>
        </div>
        <button
          type="button"
          onClick={() => onUpdate({ ...regionFilter, enabled: !regionFilter.enabled })}
          className={`w-10 rounded-full transition-all flex-shrink-0 ${
            regionFilter.enabled ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
          }`}
          style={{ height: 20 }}
        >
          <div
            className={`rounded-full bg-white shadow transition-transform ${
              regionFilter.enabled ? 'translate-x-5' : 'translate-x-0.5'
            }`}
            style={{ width: 18, height: 18, marginTop: 1 }}
          />
        </button>
      </div>

      {regionFilter.enabled && (
        <div className="space-y-3 pl-1 mt-3">
          {/* Countries */}
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Countries</label>
            <div className="flex gap-1 flex-wrap mb-1">
              {(regionFilter.countries || []).map((c) => (
                <span
                  key={c}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 text-xs rounded-full"
                >
                  {c}
                  <button type="button" onClick={() => removeItem('countries', c)} className="hover:text-red-600">
                    &times;
                  </button>
                </span>
              ))}
            </div>
            <select
              value=""
              onChange={(e) => {
                if (e.target.value) addItem('countries', e.target.value)
              }}
              className="w-full px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
            >
              <option value="">Add country...</option>
              {COUNTRIES.filter((c) => !(regionFilter.countries || []).includes(c)).map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          {/* States/Provinces */}
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">States / Provinces</label>
            <div className="flex gap-1 flex-wrap mb-1">
              {(regionFilter.states || []).map((s) => (
                <span
                  key={s}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 text-xs rounded-full"
                >
                  {s}
                  <button type="button" onClick={() => removeItem('states', s)} className="hover:text-red-600">
                    &times;
                  </button>
                </span>
              ))}
            </div>
            <select
              value=""
              onChange={(e) => {
                if (e.target.value) addItem('states', e.target.value)
              }}
              className="w-full px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
            >
              <option value="">Add state/province...</option>
              {ALL_REGIONS.filter((s) => !(regionFilter.states || []).includes(s)).map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          {/* Cities */}
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Cities</label>
            <div className="flex gap-1 flex-wrap mb-1">
              {(regionFilter.cities || []).map((c) => (
                <span
                  key={c}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 text-xs rounded-full"
                >
                  {c}
                  <button type="button" onClick={() => removeItem('cities', c)} className="hover:text-red-600">
                    &times;
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-1">
              <input
                type="text"
                value={cityInput}
                onChange={(e) => setCityInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addItem('cities', cityInput)
                    setCityInput('')
                  }
                }}
                placeholder="Type city name and press Enter"
                className="flex-1 px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
              />
              <button
                type="button"
                onClick={() => {
                  addItem('cities', cityInput)
                  setCityInput('')
                }}
                className="px-2 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300"
              >
                Add
              </button>
            </div>
          </div>

          {(regionFilter.countries?.length || 0) === 0 &&
            (regionFilter.states?.length || 0) === 0 &&
            (regionFilter.cities?.length || 0) === 0 && (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                No regions specified — member will see all shows. Add at least one location to filter.
              </p>
            )}
        </div>
      )}
    </div>
  )
}

function DateFilterConfig({
  enabled,
  from,
  to,
  onToggle,
  onChangeFrom,
  onChangeTo,
}: {
  sectionId?: string
  enabled: boolean
  from: string
  to: string
  onToggle: (v: boolean) => void
  onChangeFrom: (v: string) => void
  onChangeTo: (v: string) => void
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-xs font-medium text-gray-600 dark:text-gray-400">
            Date Filter
          </div>
          <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            Restrict to a date range
          </div>
        </div>
        <button
          type="button"
          onClick={() => onToggle(!enabled)}
          className={`w-10 rounded-full transition-all flex-shrink-0 ${
            enabled ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
          }`}
          style={{ height: 20 }}
        >
          <div
            className={`rounded-full bg-white shadow transition-transform ${
              enabled ? 'translate-x-5' : 'translate-x-0.5'
            }`}
            style={{ width: 18, height: 18, marginTop: 1 }}
          />
        </button>
      </div>

      {enabled && (
        <div className="flex gap-2 pl-1 mt-2">
          <div className="flex-1">
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">From</label>
            <input
              type="date"
              value={from}
              onChange={(e) => onChangeFrom(e.target.value)}
              className="w-full px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">To</label>
            <input
              type="date"
              value={to}
              onChange={(e) => onChangeTo(e.target.value)}
              className="w-full px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
            />
          </div>
        </div>
      )}
    </div>
  )
}
